import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { fetchScenarios, setScenario } from "../api";
import type { ScenarioCatalogEntry } from "../types";
import { TokenDisplay } from "../components/TokenDisplay";

interface Props {
  sessionToken: string;
  imToken: string;
  onContinueAsIm: () => void;
}

type Tier = "all" | "L1" | "L2" | "L3";
const TIERS: { key: Tier; label: string }[] = [
  { key: "all", label: "All" },
  { key: "L1", label: "L1 · 30m" },
  { key: "L2", label: "L2 · 45m" },
  { key: "L3", label: "L3 · 60m" },
];

export function HostNew({ sessionToken, imToken, onContinueAsIm }: Props) {
  const [scenarios, setScenarios] = useState<ScenarioCatalogEntry[]>([]);
  const [chosen, setChosen] = useState<string>("random");
  const [query, setQuery] = useState("");
  const [tier, setTier] = useState<Tier>("all");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetchScenarios()
      .then((s) => setScenarios(s))
      .catch(() => setErr("could not load scenarios"));
  }, []);

  const random = scenarios.find((s) => s.id === "random");
  const curated = useMemo(() => scenarios.filter((s) => s.id !== "random"), [scenarios]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return curated.filter((s) => {
      if (tier !== "all" && s.difficulty !== tier) return false;
      if (!q) return true;
      return `${s.name} ${s.victimComposite} ${s.premise}`.toLowerCase().includes(q);
    });
  }, [curated, query, tier]);

  const sel = scenarios.find((s) => s.id === chosen);
  const joinUrl = `${location.origin}/?session=${encodeURIComponent(sessionToken)}`;

  const onConfirm = async () => {
    setErr("");
    setBusy(true);
    try {
      await setScenario(sessionToken, imToken, chosen);
      onContinueAsIm();
    } catch {
      setErr("could not set scenario — try again");
    } finally {
      setBusy(false);
    }
  };

  const cardStyle = (active: boolean): CSSProperties => ({
    textAlign: "left",
    border: `1px solid ${active ? "var(--color-accent)" : "var(--color-border)"}`,
    background: active ? "var(--color-bg-surface)" : "var(--color-bg)",
    padding: "10px 12px",
    cursor: "pointer",
    color: "inherit",
    font: "inherit",
    display: "block",
    width: "100%",
  });

  return (
    <div className="row">
      <section className="panel grow">
        <h2>Pick the scenario</h2>
        <p style={{ fontSize: 12, color: "var(--color-text-muted)", marginBottom: 12 }}>
          The catalogue is yours alone — players don't see scenario names or premises until the
          AAR. Search or filter, pick one, then read the IM briefing notes in the lobby.
        </p>

        {/* Random — pinned */}
        {random && (
          <button
            type="button"
            onClick={() => setChosen("random")}
            style={{ ...cardStyle(chosen === "random"), marginBottom: 12 }}
          >
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 600 }}>
              🎲 Random Incident
            </span>
            <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 4 }}>
              The server draws a fresh chain at start — even you don't know it. Good for a cold read.
            </div>
          </button>
        )}

        {/* Controls */}
        <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, sector, premise…"
            aria-label="Search scenarios"
            style={{
              flex: "1 1 200px",
              padding: "7px 10px",
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              background: "var(--color-bg)",
              border: "1px solid var(--color-border)",
              color: "var(--color-text)",
            }}
          />
          <div style={{ display: "flex", gap: 4 }}>
            {TIERS.map((t) => (
              <button key={t.key} className={tier === t.key ? "primary" : ""} onClick={() => setTier(t.key)} style={{ fontSize: 11 }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--color-text-subtle)", letterSpacing: "0.1em", marginBottom: 8 }}>
          {curated.length === 0 && !err ? "LOADING…" : `${filtered.length} OF ${curated.length} SCENARIOS`}
        </div>

        {/* Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
            gap: 8,
            maxHeight: 380,
            overflowY: "auto",
            paddingRight: 4,
          }}
        >
          {filtered.map((s) => (
            <button key={s.id} type="button" onClick={() => setChosen(s.id)} style={cardStyle(chosen === s.id)}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 12.5, fontWeight: 600 }}>{s.name}</span>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, color: "var(--color-text-subtle)", letterSpacing: "0.1em", whiteSpace: "nowrap" }}>
                  {s.difficulty} · {s.estimatedMinutes}m
                </span>
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--color-text-muted)",
                  marginTop: 4,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {s.victimComposite}
              </div>
            </button>
          ))}
          {curated.length > 0 && filtered.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--color-text-subtle)", gridColumn: "1 / -1", padding: "8px 0" }}>
              No scenarios match “{query}”{tier !== "all" ? ` in ${tier}` : ""}.
            </div>
          )}
        </div>
        {err && <div className="token-warn" style={{ marginTop: 10 }}>⚠ {err}</div>}

        {/* Selected premise (IM-only) */}
        {sel && sel.id !== "random" && (
          <div className="panel" style={{ background: "var(--color-bg-surface)", margin: "14px 0", fontSize: 12, lineHeight: 1.55 }}>
            <h3 style={{ marginTop: 0 }}>
              {sel.name} <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--color-text-subtle)", letterSpacing: "0.12em" }}>· {sel.difficulty} · {sel.estimatedMinutes}m · premise (IM-only)</span>
            </h3>
            <div style={{ color: "var(--color-text)" }}>{sel.premise}</div>
          </div>
        )}

        <button className="primary" onClick={onConfirm} disabled={busy} style={{ marginTop: 4 }}>
          {busy ? "…" : `Confirm ${sel ? sel.name : "scenario"} → continue`}
        </button>
      </section>

      <section className="panel sidebar">
        <h2>Tokens</h2>
        <p style={{ fontSize: 12, color: "var(--color-text-muted)", marginBottom: 12 }}>
          Share the SESSION token with teams. Keep the IM token to yourself.
        </p>
        <div style={{ display: "grid", gap: 14 }}>
          <TokenDisplay label="Session — shareable" token={sessionToken} />
          <TokenDisplay
            label="IM token — privileged"
            token={imToken}
            warn="Anyone with the IM token can see the hidden chain. Do not paste it in shared chat."
          />
          <TokenDisplay label="Quick-join link" token={joinUrl} />
        </div>
      </section>
    </div>
  );
}
